import Phaser from 'phaser';
import type { AnimalKey, FruitKind, LevelConfig, ThemeKey } from '../types';
import { ANIMAL_STATS } from '../config/animals';
import { LEVELS } from '../config/levels';
import { GameState, DIFFICULTY_MODS } from '../state/GameState';
import { ensureGroundTextures, applyTheme, spawnCloud } from '../render/themes';
import { THEMES } from '../config/levels';
import { makeMountWithRider } from '../render/mount';
import { makeFruit, fruitDisplayName } from '../render/fruits';
import { makeFence, makeLowBranch } from '../render/obstacles';
import { InputManager } from '../input/InputManager';

const GROUND_Y = 600;
const GRAVITY = 1800;
const DUCK_TAP_DURATION_MS = 350;
const SLOW_FACTOR = 0.5;
const INVULN_MS = 900;
const SPEED_SCALE = 0.55;
// Minimum horizontal distance (in world pixels) between two fences. Anything
// less and the player can't physically jump → land → jump again in time.
const MIN_FENCE_GAP_PX = 600;

type ObstacleKind = 'fence' | 'branch' | 'fruit';

interface ObstacleData {
  container: Phaser.GameObjects.Container;
  body: Phaser.Physics.Arcade.Body;
  kind: ObstacleKind;
  fruitKind?: FruitKind;
}

export class GameScene extends Phaser.Scene {
  private level!: number;
  private levelConfig!: LevelConfig;
  private animalKey!: AnimalKey;

  private mount!: Phaser.GameObjects.Container;
  private mountBody!: Phaser.Physics.Arcade.Body;
  private cloudGroup!: Phaser.GameObjects.Group;
  private groundTile!: Phaser.GameObjects.TileSprite;
  private hillsTile: Phaser.GameObjects.TileSprite | null = null;
  private decoTile: Phaser.GameObjects.TileSprite | null = null;

  private input2!: InputManager;
  private speed = 0;
  private targetSpeedFactor = 1;

  private obstacles: ObstacleData[] = [];
  private nextSpawn = 0;

  private fruitsThisLevel = 0;
  private hp = 1;
  private invulnUntil = 0;
  private gameOver = false;
  private levelComplete = false;
  private paused = false;
  private pauseOverlay?: Phaser.GameObjects.Container;


  private isDucking = false;
  private duckPulseUntil = 0;
  private isHolding = false;
  private originalBodySize!: { w: number; h: number; ox: number; oy: number };

  private hudText!: Phaser.GameObjects.Text;
  private fruitText!: Phaser.GameObjects.Text;
  private hearts: Phaser.GameObjects.Graphics[] = [];

  constructor() {
    super('GameScene');
  }

  init(data: { level?: number }): void {
    this.level = data.level ?? GameState.getLevel(this.registry) ?? 1;
    GameState.setLevel(this.registry, this.level);
    this.levelConfig = LEVELS[this.level];
    this.animalKey = GameState.getAnimal(this.registry);

    GameState.resetRunStats(this.registry);
    // Hearts come from the chosen difficulty (4/3/2 for easy/medium/hard).
    const difficulty = GameState.getDifficulty(this.registry);
    this.hp = DIFFICULTY_MODS[difficulty].hearts;
    this.fruitsThisLevel = 0;
    this.gameOver = false;
    this.levelComplete = false;
    this.isDucking = false;
    this.isHolding = false;
    this.duckPulseUntil = 0;
    this.invulnUntil = 0;
    this.targetSpeedFactor = 1;
    this.obstacles = [];
    this.nextSpawn = 0;
  }

  create(): void {
    ensureGroundTextures(this);

    const themeKey: ThemeKey = this.levelConfig.theme;
    const parts = applyTheme(this, themeKey, GROUND_Y);
    this.groundTile = parts.groundTile;
    this.cloudGroup = parts.cloudGroup;
    this.hillsTile = parts.hills;
    this.decoTile = parts.decorations;

    // Ocean: hide clouds (no clouds underwater).
    if (themeKey === 'river') this.cloudGroup.setVisible(false);

    // Ocean / underwater level: full-screen blue tint + drifting bubbles +
    // god-ray light shafts. The "sky" gradient is already deep blue from the
    // theme, so we just add atmosphere.
    if (themeKey === 'river') {
      const W = this.scale.width;
      const H = this.scale.height;
      // Light shafts streaming down from the surface
      const shafts = this.add.graphics().setDepth(2);
      shafts.fillStyle(0xffffff, 0.06);
      for (let i = 0; i < 5; i++) {
        const sx = (i / 5) * W + Phaser.Math.Between(-40, 40);
        shafts.fillTriangle(sx, 0, sx - 60, H, sx + 60, H);
      }
      // Drifting bubbles
      for (let i = 0; i < 18; i++) {
        const bx = Phaser.Math.Between(0, W);
        const by = Phaser.Math.Between(0, H);
        const r = Phaser.Math.Between(2, 6);
        const bubble = this.add.circle(bx, by, r, 0xffffff, 0.4).setDepth(3);
        this.tweens.add({
          targets: bubble,
          y: -10,
          duration: Phaser.Math.Between(4000, 9000),
          repeat: -1,
          onRepeat: () => {
            bubble.x = Phaser.Math.Between(0, W);
            bubble.y = H + 10;
          },
        });
      }
    }

    // Forest: rainy / overcast — slow-moving heavy clouds + diagonal rain.
    // Mountains / snowy: gentle falling snow.
    if (themeKey === 'mountains' || themeKey === 'snowy') {
      const W = this.scale.width;
      const H = this.scale.height;
      const snowGfx = this.add.graphics().setDepth(8);
      const flakeCount = 70;
      const flakes: { x: number; y: number; r: number; speed: number; drift: number }[] = [];
      for (let i = 0; i < flakeCount; i++) {
        flakes.push({
          x: Phaser.Math.Between(0, W),
          y: Phaser.Math.Between(0, H),
          r: Phaser.Math.FloatBetween(1, 2.5),
          speed: Phaser.Math.Between(60, 130),
          drift: Phaser.Math.FloatBetween(-25, 25),
        });
      }
      this.events.on('update', (_t: number, dt: number) => {
        if (this.gameOver || this.levelComplete || this.paused) return;
        const dts = dt / 1000;
        snowGfx.clear();
        snowGfx.fillStyle(0xffffff, 0.9);
        for (const f of flakes) {
          f.y += f.speed * dts;
          f.x += f.drift * dts;
          if (f.y > H) {
            f.y = -5;
            f.x = Phaser.Math.Between(0, W);
          }
          if (f.x < -5) f.x = W + 5;
          if (f.x > W + 5) f.x = -5;
          snowGfx.fillCircle(f.x, f.y, f.r);
        }
      });
    }

    if (themeKey === 'forest') {
      const W = this.scale.width;
      const H = this.scale.height;
      // Diagonal rain lines, scrolling
      const rainGfx = this.add.graphics().setDepth(8);
      const rainCount = 80;
      const drops: { x: number; y: number; len: number; speed: number }[] = [];
      for (let i = 0; i < rainCount; i++) {
        drops.push({
          x: Phaser.Math.Between(-50, W),
          y: Phaser.Math.Between(0, H),
          len: Phaser.Math.Between(8, 16),
          speed: Phaser.Math.Between(700, 1100),
        });
      }
      this.events.on('update', (_t: number, dt: number) => {
        if (this.gameOver || this.levelComplete || this.paused) return;
        const dts = dt / 1000;
        rainGfx.clear();
        rainGfx.lineStyle(1.5, 0xc4d0e0, 0.55);
        for (const d of drops) {
          d.y += d.speed * dts;
          d.x -= d.speed * 0.3 * dts;
          if (d.y > H) {
            d.y = -10;
            d.x = Phaser.Math.Between(0, W + 60);
          }
          if (d.x < -20) d.x = W + 20;
          rainGfx.beginPath();
          rainGfx.moveTo(d.x, d.y);
          rainGfx.lineTo(d.x - d.len * 0.3, d.y + d.len);
          rainGfx.strokePath();
        }
      });
    }

    // House (indoor) theme: hide clouds, draw a wallpaper pattern + a window
    // + a framed picture so it reads as a room.
    if (themeKey === 'house') {
      this.cloudGroup.setVisible(false);
      const W = this.scale.width;
      const wallH = GROUND_Y; // wall extends down to the floor
      // Wallpaper stripes on the upper wall
      const stripes = this.add.graphics().setDepth(0);
      stripes.fillStyle(0xeed5b8, 1);
      stripes.fillRect(0, 0, W, wallH);
      stripes.fillStyle(0xd9bf95, 0.35);
      for (let x = 0; x < W; x += 50) {
        stripes.fillRect(x, 0, 24, wallH);
      }
      // Polka dots over the stripes
      stripes.fillStyle(0xfaf3e1, 0.7);
      for (let y = 30; y < wallH - 30; y += 60) {
        for (let x = 25; x < W; x += 60) {
          stripes.fillCircle(x + (y / 60) * 8, y, 3);
        }
      }
      // Skirting board / dado rail just above the floor
      const dado = this.add.graphics().setDepth(0);
      dado.fillStyle(0x8a5a2a, 1);
      dado.fillRect(0, GROUND_Y - 14, W, 4);
      dado.fillStyle(0xc69870, 1);
      dado.fillRect(0, GROUND_Y - 10, W, 6);

      // A window with sky/cloud glimpse, fixed position
      const wx = 240;
      const wy = 160;
      const window = this.add.graphics().setDepth(1);
      // frame
      window.fillStyle(0xffffff, 1);
      window.fillRect(wx - 110, wy - 80, 220, 160);
      // glass
      window.fillStyle(0x9bc8e8, 1);
      window.fillRect(wx - 100, wy - 70, 200, 140);
      // mullions (cross)
      window.fillStyle(0xffffff, 1);
      window.fillRect(wx - 2, wy - 70, 4, 140);
      window.fillRect(wx - 100, wy - 2, 200, 4);
      // little cloud
      window.fillStyle(0xffffff, 0.85);
      window.fillCircle(wx - 50, wy - 40, 12);
      window.fillCircle(wx - 30, wy - 44, 14);
      window.fillCircle(wx - 10, wy - 40, 12);
      // window sill
      window.fillStyle(0x8a5a2a, 1);
      window.fillRect(wx - 116, wy + 80, 232, 8);

      // A framed picture on the wall further along
      const fx = W - 320;
      const fy = 160;
      const frame = this.add.graphics().setDepth(1);
      frame.fillStyle(0x6b3410, 1);
      frame.fillRect(fx - 70, fy - 50, 140, 100);
      frame.fillStyle(0xa86a3a, 1);
      frame.fillRect(fx - 64, fy - 44, 128, 88);
      // Inner picture: a tiny smiley sun
      frame.fillStyle(0xffd24a, 1);
      frame.fillCircle(fx, fy, 22);
      frame.fillStyle(0x3d2914, 1);
      frame.fillCircle(fx - 7, fy - 4, 2);
      frame.fillCircle(fx + 7, fy - 4, 2);

      // A wall lamp pool of light, soft gradient ellipse
      const lamp = this.add.graphics().setDepth(1);
      lamp.fillStyle(0xfff1c4, 0.18);
      lamp.fillEllipse(W / 2, 60, W * 1.1, 220);
    }

    const character = GameState.getCharacter(this.registry);
    this.mount = makeMountWithRider(this, this.animalKey, character, 220, GROUND_Y - 50);
    this.physics.add.existing(this.mount);
    this.mountBody = this.mount.body as Phaser.Physics.Arcade.Body;

    const stats = ANIMAL_STATS[this.animalKey];
    const hb = stats.bodyHitbox;
    this.mountBody.setSize(hb.width, hb.height);
    this.mountBody.setOffset(hb.offsetX, hb.offsetY);
    this.mountBody.setCollideWorldBounds(false);
    this.mountBody.setGravityY(GRAVITY);
    this.originalBodySize = { w: hb.width, h: hb.height, ox: hb.offsetX, oy: hb.offsetY };

    const themeSpeedMult = this.levelConfig.theme === 'house' ? 0.85 : 1;
    const diffMods = DIFFICULTY_MODS[GameState.getDifficulty(this.registry)];
    this.speed =
      stats.startSpeed *
      this.levelConfig.difficulty.speedMultiplier *
      SPEED_SCALE *
      themeSpeedMult *
      diffMods.speedMult;

    // HUD — pause button at top-left, then level + fruit text shifted right.
    const pauseBtn = this.add.container(24, 18).setDepth(15);
    const pauseBg = this.add.rectangle(0, 0, 36, 32, 0x3d2914, 0.85).setStrokeStyle(2, 0xffffff).setOrigin(0, 0);
    const pauseTxt = this.add
      .text(18, 16, '❚❚', { fontFamily: 'sans-serif', fontSize: '16px', color: '#ffffff' })
      .setOrigin(0.5);
    pauseBtn.add([pauseBg, pauseTxt]);
    pauseBg.setInteractive({ useHandCursor: true });
    pauseBg.on('pointerover', () => pauseBg.setFillStyle(0x6b3410, 0.9));
    pauseBg.on('pointerout', () => pauseBg.setFillStyle(0x3d2914, 0.85));
    pauseBg.on('pointerdown', () => this.togglePause());

    this.hudText = this.add
      .text(70, 18, '', { fontFamily: 'sans-serif', fontSize: '28px', color: '#222' })
      .setDepth(10);
    this.fruitText = this.add
      .text(70, 52, '', { fontFamily: 'sans-serif', fontSize: '22px', color: '#a82a2a' })
      .setDepth(10);

    // Hearts in the top-right (above the SKIP button).
    this.hearts = [];
    for (let i = 0; i < this.hp; i++) {
      const g = this.add.graphics().setDepth(10);
      const x = this.scale.width - 32 - i * 40;
      const y = 92;
      drawHeart(g, x, y, true);
      this.hearts.push(g);
    }
    this.add
      .text(this.scale.width - 24, 60, 'SPACE/UP jump  •  DOWN tap=duck, hold=slow', {
        fontFamily: 'sans-serif',
        fontSize: '16px',
        color: '#3d2914',
      })
      .setOrigin(1, 0)
      .setDepth(10);

    // Skip button — jumps to level complete (or boss if applicable).
    const skipBtn = this.add.container(this.scale.width - 24, 18).setDepth(10);
    const skipBg = this.add.rectangle(0, 0, 90, 32, 0x3d2914, 0.85).setStrokeStyle(2, 0xffffff).setOrigin(1, 0);
    const skipTxt = this.add
      .text(-45, 16, 'SKIP ▶', { fontFamily: 'sans-serif', fontSize: '16px', color: '#ffffff' })
      .setOrigin(0.5, 0.5);
    skipBtn.add([skipBg, skipTxt]);
    skipBg.setInteractive({ useHandCursor: true });
    skipBg.on('pointerover', () => skipBg.setFillStyle(0x6b3410, 0.9));
    skipBg.on('pointerout', () => skipBg.setFillStyle(0x3d2914, 0.85));
    skipBg.on('pointerdown', () => {
      if (this.gameOver || this.levelComplete) return;
      this.fruitsThisLevel = this.levelConfig.target;
      const stats = GameState.getRunStats(this.registry);
      GameState.setRunStats(this.registry, { ...stats, fruitsThisLevel: this.fruitsThisLevel });
      this.completeLevel();
    });

    this.refreshHud();

    // Input
    this.input2 = new InputManager(this);
    this.input2.onJump(() => this.tryJump());
    this.input2.onDuckTap(() => this.beginDuckPulse());
    this.input2.onDuckHoldStart(() => this.beginHold());
    this.input2.onDuckHoldEnd(() => this.endHold());

    // On-screen buttons (bottom-right) for phone play. Only shown on touch
    // devices — desktop uses the keyboard. Buttons are also larger on touch
    // so they're easy to tap.
    if (isTouchDevice()) {
      const r = 72; // big tap target
      const gap = 60; // space between the two buttons
      const downY = this.scale.height - r - 30;
      const upY = downY - r * 2 - gap;
      this.makeTouchButton(
        this.scale.width - r - 30,
        upY,
        '▲',
        r,
        () => this.input2.triggerJump(),
      );
      this.makeTouchButton(
        this.scale.width - r - 30,
        downY,
        '▼',
        r,
        undefined,
        () => this.input2.pressDown(),
        () => this.input2.releaseDown(),
      );
    }

    // Level intro banner
    const banner = this.add
      .text(
        this.scale.width / 2,
        140,
        `Level ${this.level} — ${this.levelConfig.theme}\nCollect ${this.levelConfig.target} ${fruitDisplayName(this.levelConfig.fruit)}`,
        { fontFamily: 'sans-serif', fontSize: '34px', color: '#222', align: 'center' },
      )
      .setOrigin(0.5)
      .setDepth(20);
    this.tweens.add({ targets: banner, alpha: 0, delay: 1500, duration: 600, onComplete: () => banner.destroy() });
  }

  update(_time: number, delta: number): void {
    if (this.gameOver || this.levelComplete || this.paused) return;
    const dt = delta / 1000;
    this.input2.update();

    const stats = ANIMAL_STATS[this.animalKey];
    const themeSpeedMult = this.levelConfig.theme === 'house' ? 0.85 : 1;
    const diffSpeedMult = DIFFICULTY_MODS[GameState.getDifficulty(this.registry)].speedMult;
    const maxSpeed =
      stats.maxSpeed *
      this.levelConfig.difficulty.speedMultiplier *
      SPEED_SCALE *
      themeSpeedMult *
      diffSpeedMult;
    const targetSpeed = maxSpeed * this.targetSpeedFactor;
    if (this.speed < targetSpeed) {
      this.speed = Math.min(targetSpeed, this.speed + stats.speedRamp * dt * 16);
    } else if (this.speed > targetSpeed) {
      this.speed = Math.max(targetSpeed, this.speed - stats.speedRamp * dt * 60);
    }

    // Scroll background
    this.groundTile.tilePositionX += this.speed * dt;
    if (this.hillsTile) this.hillsTile.tilePositionX += this.speed * 0.18 * dt;
    if (this.decoTile) this.decoTile.tilePositionX += this.speed * 0.55 * dt;
    this.cloudGroup.getChildren().forEach((c) => {
      const cloud = c as Phaser.GameObjects.Container;
      cloud.x -= this.speed * 0.15 * dt;
      if (cloud.x < -120) {
        cloud.x = this.scale.width + 120;
        cloud.y = Phaser.Math.Between(60, 220);
        if (this.cloudGroup.getLength() < 4) {
          spawnCloud(
            this,
            this.cloudGroup,
            this.scale.width + 120,
            THEMES[this.levelConfig.theme].cloudColor,
            THEMES[this.levelConfig.theme].cloudAlpha,
          );
        }
      }
    });

    // End duck-pulse window
    if (this.isDucking && !this.isHolding && this.time.now > this.duckPulseUntil) {
      this.exitDuck();
    }

    // Mount ground-snap and rotation
    const onGround = this.mount.y >= GROUND_Y - 50 - 0.5;
    if (onGround && this.mountBody.velocity.y >= 0) {
      this.mount.y = GROUND_Y - 50;
      this.mountBody.setVelocityY(0);
      this.mount.rotation = Math.sin(this.time.now / 80) * 0.04;
    } else {
      this.mount.rotation = Phaser.Math.Clamp(this.mountBody.velocity.y / 1200, -0.4, 0.5);
    }

    // Move and clean obstacles + collision check
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      const ob = this.obstacles[i];
      ob.container.x -= this.speed * dt;
      if (ob.container.x < -120) {
        ob.container.destroy();
        this.obstacles.splice(i, 1);
        continue;
      }
      // overlap check (manual — physics overlap with arrays of containers is fiddly)
      if (this.checkOverlap(ob)) {
        this.handleHit(ob);
        if (ob.container.active) {
          ob.container.destroy();
        }
        this.obstacles.splice(i, 1);
        if (this.gameOver || this.levelComplete) return;
      }
    }

    // Spawn loop
    this.nextSpawn -= delta;
    if (this.nextSpawn <= 0) {
      this.spawnObstacle();
      const { spawnMinGap, spawnMaxGap } = this.levelConfig.difficulty;
      this.nextSpawn = Phaser.Math.Between(spawnMinGap, spawnMaxGap);
    }
  }

  private tryJump(): void {
    if (this.gameOver || this.levelComplete) return;
    if (this.isDucking || this.isHolding) return;
    const onGround = this.mount.y >= GROUND_Y - 51;
    if (onGround) {
      const stats = ANIMAL_STATS[this.animalKey];
      this.mountBody.setVelocityY(stats.jumpVelocity);
      this.cameras.main.shake(60, 0.002);
    }
  }

  private beginDuckPulse(): void {
    if (this.gameOver || this.levelComplete) return;
    if (!this.isOnGround()) return;
    if (this.isHolding) return;
    this.enterDuck();
    this.duckPulseUntil = this.time.now + DUCK_TAP_DURATION_MS;
  }

  private beginHold(): void {
    if (this.gameOver || this.levelComplete) return;
    if (!this.isOnGround()) return;
    this.isHolding = true;
    this.enterDuck();
    this.targetSpeedFactor = SLOW_FACTOR;
  }

  private endHold(): void {
    this.isHolding = false;
    this.targetSpeedFactor = 1;
    if (this.isDucking && this.time.now > this.duckPulseUntil) {
      this.exitDuck();
    }
  }

  private enterDuck(): void {
    if (this.isDucking) return;
    this.isDucking = true;
    const stats = ANIMAL_STATS[this.animalKey];
    const newH = this.originalBodySize.h * stats.duckHitboxScale;
    const newOy = this.originalBodySize.oy + (this.originalBodySize.h - newH);
    this.mountBody.setSize(this.originalBodySize.w, newH);
    this.mountBody.setOffset(this.originalBodySize.ox, newOy);
    this.mount.scaleY = 0.7;
  }

  private exitDuck(): void {
    if (!this.isDucking) return;
    this.isDucking = false;
    this.mountBody.setSize(this.originalBodySize.w, this.originalBodySize.h);
    this.mountBody.setOffset(this.originalBodySize.ox, this.originalBodySize.oy);
    this.mount.scaleY = 1;
  }

  private isOnGround(): boolean {
    return this.mount.y >= GROUND_Y - 51;
  }

  private spawnObstacle(): void {
    const W = this.scale.width;
    const x = W + 100;
    const r = Math.random();
    const { fruitChance, duckChance } = this.levelConfig.difficulty;

    if (r < fruitChance) {
      const y = Math.random() < 0.5 ? GROUND_Y - 40 : GROUND_Y - 110;
      this.spawnFruit(x, y);
    } else if (r < fruitChance + duckChance) {
      this.spawnBranch(x);
    } else if (this.canSpawnFenceAt(x)) {
      this.spawnFence(x);
    } else {
      // Too close to last fence — drop in a fruit so the spawn slot isn't wasted.
      const y = Math.random() < 0.5 ? GROUND_Y - 40 : GROUND_Y - 110;
      this.spawnFruit(x, y);
    }
  }

  private canSpawnFenceAt(x: number): boolean {
    for (const ob of this.obstacles) {
      if (ob.kind !== 'fence') continue;
      if (Math.abs(x - ob.container.x) < MIN_FENCE_GAP_PX) return false;
    }
    return true;
  }

  private spawnFence(x: number): void {
    const c = makeFence(this, x, GROUND_Y, this.levelConfig.theme);
    this.physics.add.existing(c);
    const body = c.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    if (this.levelConfig.theme === 'river') {
      // Seaweed clump.
      body.setSize(60, 60);
      body.setOffset(-30, -50);
    } else if (this.levelConfig.theme === 'house') {
      // Toy on the floor — short and wide.
      body.setSize(50, 26);
      body.setOffset(-25, -16);
    } else if (this.levelConfig.theme === 'mountains' || this.levelConfig.theme === 'snowy') {
      // Snow mound — wider, shorter than a fence.
      body.setSize(64, 36);
      body.setOffset(-32, -22);
    } else if (this.levelConfig.theme === 'desert') {
      // Hay bale — wide and chunky.
      body.setSize(70, 44);
      body.setOffset(-35, -28);
    } else {
      body.setSize(50, 56);
      body.setOffset(-25, -28);
    }
    this.obstacles.push({ container: c, body, kind: 'fence' });
  }

  private spawnBranch(x: number): void {
    const c = makeLowBranch(this, x, GROUND_Y, this.levelConfig.theme);
    this.physics.add.existing(c);
    const body = c.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    body.setSize(80, 28);
    body.setOffset(-40, -14);
    this.obstacles.push({ container: c, body, kind: 'branch' });
  }

  private spawnFruit(x: number, y: number): void {
    const fruitKind = this.levelConfig.fruit;
    const c = makeFruit(this, fruitKind, x, y);
    this.physics.add.existing(c);
    const body = c.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    body.setCircle(18, -18, -18);
    this.obstacles.push({ container: c, body, kind: 'fruit', fruitKind });
    this.tweens.add({
      targets: c,
      y: y - 10,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private checkOverlap(ob: ObstacleData): boolean {
    const a = this.mountBody;
    const b = ob.body;
    return (
      a.x < b.x + b.width &&
      a.x + a.width > b.x &&
      a.y < b.y + b.height &&
      a.y + a.height > b.y
    );
  }

  private handleHit(ob: ObstacleData): void {
    if (ob.kind === 'fruit') {
      this.fruitsThisLevel += 1;
      // Also persist the running count so BossScene can use it as Heal stock.
      const stats = GameState.getRunStats(this.registry);
      GameState.setRunStats(this.registry, { ...stats, fruitsThisLevel: this.fruitsThisLevel });
      this.refreshHud();
      const burst = this.add.circle(ob.container.x, ob.container.y, 24, 0xffe066, 0.8);
      this.tweens.add({
        targets: burst,
        radius: 60,
        alpha: 0,
        duration: 300,
        onComplete: () => burst.destroy(),
      });
      if (this.fruitsThisLevel >= this.levelConfig.target) {
        this.completeLevel();
      }
      return;
    }
    // hostile (fence or branch)
    if (this.time.now < this.invulnUntil) return;
    this.hp -= 1;
    this.invulnUntil = this.time.now + INVULN_MS;
    this.cameras.main.flash(150, 200, 50, 50);
    this.cameras.main.shake(120, 0.008);
    this.flashMount();
    this.refreshHud();
    if (this.hp <= 0) {
      this.endGame();
    }
  }

  private flashMount(): void {
    this.tweens.add({
      targets: this.mount,
      alpha: 0.3,
      duration: 120,
      yoyo: true,
      repeat: 3,
      onComplete: () => (this.mount.alpha = 1),
    });
  }

  private completeLevel(): void {
    if (this.levelComplete) return;
    this.levelComplete = true;
    this.targetSpeedFactor = 1;
    const goal = this.add
      .text(this.scale.width / 2, this.scale.height / 2, 'GOAL!', {
        fontFamily: 'sans-serif',
        fontSize: '72px',
        color: '#f2c14e',
        stroke: '#3d2914',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setDepth(20);
    this.tweens.add({ targets: goal, scale: 1.3, duration: 600, yoyo: true });

    this.time.delayedCall(1200, () => {
      const nextLevel = this.level + 1;
      if (this.levelConfig.hasBossAtEnd) {
        this.scene.start('BossScene', { level: this.level });
      } else {
        // After a normal level, drop into a 2-question times-tables bonus
        // before continuing to the next level.
        this.scene.start('TimesTableScene', {
          next: 'LevelIntroScene',
          nextData: { level: nextLevel },
        });
      }
    });
  }

  private endGame(): void {
    if (this.gameOver) return;
    this.gameOver = true;
    this.physics.pause();
    this.cameras.main.shake(250, 0.012);
    this.time.delayedCall(600, () => {
      this.scene.start('GameOverScene', { level: this.level });
    });
  }

  private togglePause(): void {
    if (this.gameOver || this.levelComplete) return;
    this.paused = !this.paused;
    if (this.paused) {
      this.physics.pause();
      const W = this.scale.width;
      const H = this.scale.height;
      this.pauseOverlay = this.add.container(W / 2, H / 2).setDepth(30);
      const dim = this.add.rectangle(0, 0, W, H, 0x000000, 0.55);
      const card = this.add.rectangle(0, 0, 360, 240, 0x3d2914).setStrokeStyle(3, 0xffffff);
      const title = this.add
        .text(0, -80, 'Paused', { fontFamily: 'sans-serif', fontSize: '36px', color: '#ffffff' })
        .setOrigin(0.5);
      const hint = this.add
        .text(0, -34, 'Tap ❚❚ to resume', {
          fontFamily: 'sans-serif',
          fontSize: '18px',
          color: '#ffd9a0',
        })
        .setOrigin(0.5);
      // Resume button
      const resumeBg = this.add.rectangle(0, 16, 220, 40, 0x4a8a3a).setStrokeStyle(2, 0xffffff);
      const resumeTxt = this.add
        .text(0, 16, 'Resume', { fontFamily: 'sans-serif', fontSize: '20px', color: '#fff' })
        .setOrigin(0.5);
      resumeBg.setInteractive({ useHandCursor: true });
      resumeBg.on('pointerup', () => this.togglePause());
      // Exit-to-menu button
      const exitBg = this.add.rectangle(0, 70, 220, 40, 0x6b1a1a).setStrokeStyle(2, 0xffffff);
      const exitTxt = this.add
        .text(0, 70, 'Exit to menu', {
          fontFamily: 'sans-serif',
          fontSize: '20px',
          color: '#fff',
        })
        .setOrigin(0.5);
      exitBg.setInteractive({ useHandCursor: true });
      exitBg.on('pointerup', () => this.scene.start('TitleScene'));
      this.pauseOverlay.add([dim, card, title, hint, resumeBg, resumeTxt, exitBg, exitTxt]);
    } else {
      this.physics.resume();
      this.pauseOverlay?.destroy();
      this.pauseOverlay = undefined;
    }
  }

  private makeTouchButton(
    x: number,
    y: number,
    glyph: string,
    radius = 44,
    onTap?: () => void,
    onPress?: () => void,
    onRelease?: () => void,
  ): void {
    const fontSize = Math.round(radius * 0.9);
    const bg = this.add
      .circle(x, y, radius, 0x3d2914, 0.75)
      .setStrokeStyle(4, 0xffffff)
      .setDepth(15)
      .setInteractive({ useHandCursor: true });
    this.add
      .text(x, y, glyph, {
        fontFamily: 'sans-serif',
        fontSize: `${fontSize}px`,
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setDepth(16);
    bg.on('pointerdown', () => {
      bg.setFillStyle(0x6b3410, 0.9);
      onTap?.();
      onPress?.();
    });
    bg.on('pointerup', () => {
      bg.setFillStyle(0x3d2914, 0.7);
      onRelease?.();
    });
    bg.on('pointerout', () => {
      bg.setFillStyle(0x3d2914, 0.7);
      onRelease?.();
    });
  }

  private refreshHud(): void {
    const t = this.levelConfig.theme;
    const themeLabel = t === 'river' ? 'ocean' : t;
    this.hudText.setText(`Level ${this.level} — ${themeLabel}`);
    this.fruitText.setText(
      `${fruitDisplayName(this.levelConfig.fruit)}: ${this.fruitsThisLevel}/${this.levelConfig.target}`,
    );
    this.hearts.forEach((g, i) => {
      const x = this.scale.width - 32 - i * 40;
      const y = 92;
      drawHeart(g, x, y, i < this.hp);
    });
  }
}

function isTouchDevice(): boolean {
  // True on phones / tablets — false on desktop with mouse + keyboard.
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    (navigator.maxTouchPoints ?? 0) > 0 ||
    window.matchMedia('(pointer: coarse)').matches
  );
}

function drawHeart(g: Phaser.GameObjects.Graphics, cx: number, cy: number, full: boolean): void {
  g.clear();
  const fill = full ? 0xc23b3b : 0x3d2914;
  const alpha = full ? 1 : 0.45;
  g.fillStyle(fill, alpha);
  g.lineStyle(2, 0x3d2914, 1);
  // Two top circles + bottom triangle = heart shape.
  g.fillCircle(cx - 7, cy - 4, 8);
  g.fillCircle(cx + 7, cy - 4, 8);
  g.fillTriangle(cx - 14, cy - 1, cx + 14, cy - 1, cx, cy + 14);
  // Outline
  g.strokeCircle(cx - 7, cy - 4, 8);
  g.strokeCircle(cx + 7, cy - 4, 8);
  g.strokeTriangle(cx - 14, cy - 1, cx + 14, cy - 1, cx, cy + 14);
}
